// src/pages/RegistroSocios.jsx

import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import socioService from "../services/socioService";
import "../styles/registro-socios.css";

const RegistroSocios = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicRegistroRoute = location.pathname === "/registro-socios";

  // 🎨 Estados de UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  
  // 📸 Estado para la foto
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);

  // 📋 Datos personales
  const [datosPersonales, setDatosPersonales] = useState({
    apellidos: "",
    nombres: "",
    fecha_nac: "",
    lugar_nac: "",
    ciudad: "",
    nacionalidad: "",
    ci: "",
    complemento: "",
    expedido: "",
    telefono: "",
    celular: "",
    domicilio: "",
    email: "",
    nreg_profesional: "",
  });

  const departamentos = [
    "La Paz",
    "Cochabamba",
    "Santa Cruz",
    "Oruro",
    "Potosí",
    "Chuquisaca",
    "Tarija",
    "Beni",
    "Pando",
  ];

  // 📚 Estado de Formaciones Académicas
  const [formaciones, setFormaciones] = useState([
    {
      institucion: "",
      titulo_acad: "",
      n_titulo_acad: "",
      anio: "",
      titulo_pro: "",
      n_titulo_pro: "",
      anio_tp: "",
      ciudad: "",
      pais: "",
    },
  ]);

  // 📖 Estado de Estudios Relacionados
  const [estudios, setEstudios] = useState([
    {
      institucion: "",
      nombre_curso: "",
      finicio: "",
      ffinal: "",
      ciudad: "",
      pais: "",
      anio: "",
    },
  ]);

  // 📄 Estado de Publicaciones
  const [publicaciones, setPublicaciones] = useState([
    {
      titulo: "",
      revista: "",
      volumen: "",
      anio: "",
    },
  ]);

  // 💼 Estado de Puestos Desempeñados
  const [puestos, setPuestos] = useState([
    {
      puesto: "",
      institucion_empresa: "",
      unidad_proy: "",
      desde: "",
      hasta: "",
    },
  ]);

  /**
   * 🔄 Handler para cambios en datos personales
   */
  const handleChangeDatosPersonales = (e) => {
    const { name, value } = e.target;
    setDatosPersonales((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * 📸 Handler para la foto
   */
  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Validar tipo de archivo
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setError('Solo se permiten imágenes JPG, JPEG o PNG');
        setOpenSnackbar(true);
        return;
      }

      // Validar tamaño (máximo 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError('La imagen no debe superar los 5MB');
        setOpenSnackbar(true);
        return;
      }

      // Guardar el archivo
      setFotoFile(file);

      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * 🗑️ Eliminar foto seleccionada
   */
  const handleRemoverFoto = () => {
    setFotoFile(null);
    setFotoPreview(null);
    // Resetear el input file
    const fileInput = document.getElementById('foto-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  /**
   * 📚 Handlers para Formaciones
   */
  const agregarFormacion = () => {
    setFormaciones([
      ...formaciones,
      {
        institucion: "",
        titulo_acad: "",
        n_titulo_acad: "",
        anio: "",
        titulo_pro: "",
        n_titulo_pro: "",
        anio_tp: "",
        ciudad: "",
        pais: "",
      },
    ]);
  };

  const eliminarFormacion = (index) => {
    setFormaciones(formaciones.filter((_, i) => i !== index));
  };

  const handleChangeFormacion = (index, field, value) => {
    const nuevasFormaciones = [...formaciones];
    nuevasFormaciones[index][field] = value;
    setFormaciones(nuevasFormaciones);
  };

  /**
   * 📖 Handlers para Estudios
   */
  const agregarEstudio = () => {
    setEstudios([
      ...estudios,
      {
        institucion: "",
        nombre_curso: "",
        finicio: "",
        ffinal: "",
        ciudad: "",
        pais: "",
        anio: "",
      },
    ]);
  };

  const eliminarEstudio = (index) => {
    setEstudios(estudios.filter((_, i) => i !== index));
  };

  const handleChangeEstudio = (index, field, value) => {
    const nuevosEstudios = [...estudios];
    nuevosEstudios[index][field] = value;
    setEstudios(nuevosEstudios);
  };

  /**
   * 📄 Handlers para Publicaciones
   */
  const agregarPublicacion = () => {
    setPublicaciones([
      ...publicaciones,
      { titulo: "", revista: "", volumen: "", anio: "" },
    ]);
  };

  const eliminarPublicacion = (index) => {
    setPublicaciones(publicaciones.filter((_, i) => i !== index));
  };

  const handleChangePublicacion = (index, field, value) => {
    const nuevasPublicaciones = [...publicaciones];
    nuevasPublicaciones[index][field] = value;
    setPublicaciones(nuevasPublicaciones);
  };

  /**
   * 💼 Handlers para Puestos
   */
  const agregarPuesto = () => {
    setPuestos([
      ...puestos,
      {
        puesto: "",
        institucion_empresa: "",
        unidad_proy: "",
        desde: "",
        hasta: "",
      },
    ]);
  };

  const eliminarPuesto = (index) => {
    setPuestos(puestos.filter((_, i) => i !== index));
  };

  const handleChangePuesto = (index, field, value) => {
    const nuevosPuestos = [...puestos];
    nuevosPuestos[index][field] = value;
    setPuestos(nuevosPuestos);
  };

  /**
   * ✅ Validación del formulario
   */
  const validarFormulario = () => {
    // Validar campos obligatorios de datos personales
    if (!datosPersonales.apellidos || !datosPersonales.nombres) {
      setError("Apellidos y Nombres son obligatorios");
      return false;
    }

    if (!datosPersonales.email) {
      setError("El correo electrónico es obligatorio");
      return false;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(datosPersonales.email)) {
      setError("Por favor ingresa un correo electrónico válido");
      return false;
    }

    return true;
  };

  /**
   * 📤 Handler del submit del formulario
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    // Validar formulario
    if (!validarFormulario()) {
      return;
    }

    setLoading(true);

    try {
      // Preparar datos completos
      const datosCompletos = {
        datosPersonales,
        formaciones,
        estudios,
        publicaciones,
        puestos,
      };

      // Registrar socio completo con foto
      const resultado = await socioService.registrarSocioCompleto(
        datosCompletos,
        fotoFile // Pasar la foto
      );

      console.log("Registro exitoso:", resultado);

      // Mostrar mensaje de éxito
      setSuccess(true);
      setOpenSnackbar(true);

      // En la ruta pública, volver al home para mejor UX.
      // Dentro del dashboard se mantiene el reseteo del formulario.
      setTimeout(() => {
        if (isPublicRegistroRoute) {
          navigate("/");
          return;
        }
        resetearFormulario();
      }, 2000);
    } catch (err) {
      console.error("Error en el registro:", err);
      setError(err.message || "Error al registrar socio. Por favor intenta de nuevo.");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 🔄 Resetear formulario
   */
  const resetearFormulario = () => {
    setDatosPersonales({
      apellidos: "",
      nombres: "",
      fecha_nac: "",
      lugar_nac: "",
      ciudad: "",
      nacionalidad: "",
      ci: "",
      complemento: "",
      expedido: "",
      telefono: "",
      celular: "",
      domicilio: "",
      email: "",
      nreg_profesional: "",
    });

    setFormaciones([
      {
        institucion: "",
        titulo_acad: "",
        n_titulo_acad: "",
        anio: "",
        titulo_pro: "",
        n_titulo_pro: "",
        anio_tp: "",
        ciudad: "",
        pais: "",
      },
    ]);

    setEstudios([
      {
        institucion: "",
        nombre_curso: "",
        finicio: "",
        ffinal: "",
        ciudad: "",
        pais: "",
        anio: "",
      },
    ]);

    setPublicaciones([{ titulo: "", revista: "", volumen: "", anio: "" }]);

    setPuestos([
      {
        puesto: "",
        institucion_empresa: "",
        unidad_proy: "",
        desde: "",
        hasta: "",
      },
    ]);
    
    // Resetear foto
    setFotoFile(null);
    setFotoPreview(null);
    const fileInput = document.getElementById('foto-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <div className="registro-container">
      <form className="registro-form" onSubmit={handleSubmit}>
        <h2>Registro de Socios</h2>

        {/* === DATOS PERSONALES === */}
        <h3>Datos Personales</h3>

        {/* === Foto === */}
        <div className="form-row">
          <div className="form-group col-3">
            <label>Foto (3x3 cm fondo azul)</label>
            <input
              id="foto-input"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFotoChange}
            />
            {fotoPreview && (
              <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                <img
                  src={fotoPreview}
                  alt="Preview"
                  style={{
                    width: '150px',
                    height: '150px',
                    objectFit: 'cover',
                    border: '2px solid #ccc',
                    borderRadius: '4px',
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={handleRemoverFoto}
                  sx={{ mt: 1, display: 'block' }}
                >
                  Remover foto
                </Button>
              </Box>
            )}
          </div>
          <div className="form-group col-1 legend">
            <Typography variant="caption" color="text.secondary">
              Formatos permitidos: JPG, JPEG, PNG
              <br />
              Tamaño máximo: 5MB
            </Typography>
          </div>
        </div>

        {/* Apellidos y Nombres */}
        <div className="form-row">
          <div className="form-group col-2">
            <label>Apellidos *</label>
            <input
              type="text"
              name="apellidos"
              value={datosPersonales.apellidos}
              onChange={handleChangeDatosPersonales}
              required
            />
          </div>
          <div className="form-group col-2">
            <label>Nombres *</label>
            <input
              type="text"
              name="nombres"
              value={datosPersonales.nombres}
              onChange={handleChangeDatosPersonales}
              required
            />
          </div>
        </div>

        {/* Fecha, Lugar, Ciudad, Nacionalidad */}
        <div className="form-row">
          <div className="form-group col-1">
            <label>Fecha de Nacimiento</label>
            <input
              type="date"
              name="fecha_nac"
              value={datosPersonales.fecha_nac}
              onChange={handleChangeDatosPersonales}
            />
          </div>
          <div className="form-group col-1">
            <label>Lugar de Nacimiento</label>
            <select
              name="lugar_nac"
              value={datosPersonales.lugar_nac}
              onChange={handleChangeDatosPersonales}
            >
              <option value="">Seleccione...</option>
              {departamentos.map((dpto, i) => (
                <option key={i} value={dpto}>
                  {dpto}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-1">
            <label>Ciudad</label>
            <input
              type="text"
              name="ciudad"
              value={datosPersonales.ciudad}
              onChange={handleChangeDatosPersonales}
            />
          </div>
          <div className="form-group col-1">
            <label>Nacionalidad</label>
            <input
              type="text"
              name="nacionalidad"
              value={datosPersonales.nacionalidad}
              onChange={handleChangeDatosPersonales}
            />
          </div>
        </div>

        {/* CI, Complemento, Expedido, Tel, Cel */}
        <div className="form-row">
          <div className="form-group col-1">
            <label>Doc. Identidad</label>
            <input
              type="text"
              name="ci"
              value={datosPersonales.ci}
              onChange={handleChangeDatosPersonales}
            />
          </div>
          <div className="form-group col-1 half-pair">
            <div>
              <label>Com.</label>
              <input
                type="text"
                name="complemento"
                value={datosPersonales.complemento}
                onChange={handleChangeDatosPersonales}
              />
            </div>
            <div>
              <label>Exp.</label>
              <input
                type="text"
                name="expedido"
                value={datosPersonales.expedido}
                onChange={handleChangeDatosPersonales}
              />
            </div>
          </div>
          <div className="form-group col-1">
            <label>Teléfono</label>
            <input
              type="text"
              name="telefono"
              value={datosPersonales.telefono}
              onChange={handleChangeDatosPersonales}
            />
          </div>
          <div className="form-group col-1">
            <label>Celular</label>
            <input
              type="text"
              name="celular"
              value={datosPersonales.celular}
              onChange={handleChangeDatosPersonales}
            />
          </div>
        </div>

        {/* Domicilio */}
        <div className="form-row">
          <div className="form-group col-4">
            <label>Domicilio</label>
            <input
              type="text"
              name="domicilio"
              value={datosPersonales.domicilio}
              onChange={handleChangeDatosPersonales}
            />
          </div>
        </div>

        {/* Correo */}
        <div className="form-row">
          <div className="form-group col-3">
            <label>Correo Electrónico *</label>
            <input
              type="email"
              name="email"
              value={datosPersonales.email}
              onChange={handleChangeDatosPersonales}
              required
            />
          </div>
        </div>

        {/* Registro Profesional */}
        <div className="form-row">
          <div className="form-group col-3">
            <label>N° de Registro Profesional</label>
            <input
              type="text"
              name="nreg_profesional"
              value={datosPersonales.nreg_profesional}
              onChange={handleChangeDatosPersonales}
            />
          </div>
        </div>

        {/* === FORMACIÓN ACADÉMICA === */}
        <div className="form-section">
          <h3>Formación Académica</h3>

          {formaciones.map((form, index) => (
            <div key={index} className="formacion-block">
              <div className="form-row">
                <div className="form-group col-2">
                  <label>Institución</label>
                  <input
                    type="text"
                    value={form.institucion}
                    onChange={(e) =>
                      handleChangeFormacion(index, "institucion", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1">
                  <label>Título Académico</label>
                  <input
                    type="text"
                    value={form.titulo_acad}
                    onChange={(e) =>
                      handleChangeFormacion(index, "titulo_acad", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1 half-pair">
                  <div>
                    <label>Nº Título Acad.</label>
                    <input
                      type="text"
                      value={form.n_titulo_acad}
                      onChange={(e) =>
                        handleChangeFormacion(
                          index,
                          "n_titulo_acad",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div>
                    <label>Año</label>
                    <input
                      type="text"
                      value={form.anio}
                      onChange={(e) =>
                        handleChangeFormacion(index, "anio", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-2">
                  <label>Título Profesional</label>
                  <input
                    type="text"
                    value={form.titulo_pro}
                    onChange={(e) =>
                      handleChangeFormacion(index, "titulo_pro", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1">
                  <label>Nº Título Pro.</label>
                  <input
                    type="text"
                    value={form.n_titulo_pro}
                    onChange={(e) =>
                      handleChangeFormacion(
                        index,
                        "n_titulo_pro",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div className="form-group col-1">
                  <label>Año</label>
                  <input
                    type="text"
                    value={form.anio_tp}
                    onChange={(e) =>
                      handleChangeFormacion(index, "anio_tp", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-2">
                  <label>Ciudad</label>
                  <input
                    type="text"
                    value={form.ciudad}
                    onChange={(e) =>
                      handleChangeFormacion(index, "ciudad", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1">
                  <label>País</label>
                  <input
                    type="text"
                    value={form.pais}
                    onChange={(e) =>
                      handleChangeFormacion(index, "pais", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1 botones">
                  {index === 0 ? (
                    <button
                      type="button"
                      className="btn-agregar"
                      onClick={agregarFormacion}
                    >
                      + Agregar
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-eliminar"
                      onClick={() => eliminarFormacion(index)}
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>

              <hr className="form-divider" />
            </div>
          ))}
        </div>

        {/* === ESTUDIOS RELACIONADOS === */}
        <div className="form-section">
          <h3>Estudios Realizados</h3>

          {estudios.map((est, index) => (
            <div key={index} className="formacion-block">
              <div className="form-row">
                <div className="form-group col-2">
                  <label>Institución</label>
                  <input
                    type="text"
                    value={est.institucion}
                    onChange={(e) =>
                      handleChangeEstudio(index, "institucion", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-2">
                  <label>Nombre del Curso</label>
                  <input
                    type="text"
                    value={est.nombre_curso}
                    onChange={(e) =>
                      handleChangeEstudio(index, "nombre_curso", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-1">
                  <label>Fecha Inicio</label>
                  <input
                    type="date"
                    value={est.finicio}
                    onChange={(e) =>
                      handleChangeEstudio(index, "finicio", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1">
                  <label>Fecha Fin</label>
                  <input
                    type="date"
                    value={est.ffinal}
                    onChange={(e) =>
                      handleChangeEstudio(index, "ffinal", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1">
                  <label>Ciudad</label>
                  <input
                    type="text"
                    value={est.ciudad}
                    onChange={(e) =>
                      handleChangeEstudio(index, "ciudad", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1">
                  <label>País</label>
                  <input
                    type="text"
                    value={est.pais}
                    onChange={(e) =>
                      handleChangeEstudio(index, "pais", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1">
                  <label>Año de Titulación</label>
                  <input
                    type="text"
                    value={est.anio}
                    onChange={(e) =>
                      handleChangeEstudio(index, "anio", e.target.value)
                    }
                  />
                </div>

                <div className="form-group col-1 botones">
                  {index === 0 ? (
                    <button
                      type="button"
                      className="btn-agregar"
                      onClick={agregarEstudio}
                    >
                      + Agregar
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-eliminar"
                      onClick={() => eliminarEstudio(index)}
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>

              <hr className="form-divider" />
            </div>
          ))}
        </div>

        {/* === PUBLICACIONES === */}
        <div className="form-section">
          <h3>Publicaciones</h3>

          {publicaciones.map((pub, index) => (
            <div key={index} className="formacion-block">
              <div className="form-row">
                <div className="form-group col-2">
                  <label>Título</label>
                  <input
                    type="text"
                    value={pub.titulo}
                    onChange={(e) =>
                      handleChangePublicacion(index, "titulo", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1">
                  <label>Revista / Editorial</label>
                  <input
                    type="text"
                    value={pub.revista}
                    onChange={(e) =>
                      handleChangePublicacion(index, "revista", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-1 half-pair">
                  <div>
                    <label>Volumen</label>
                    <input
                      type="text"
                      value={pub.volumen}
                      onChange={(e) =>
                        handleChangePublicacion(
                          index,
                          "volumen",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div>
                    <label>Año</label>
                    <input
                      type="text"
                      value={pub.anio}
                      onChange={(e) =>
                        handleChangePublicacion(index, "anio", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="form-row botones-row">
                {index === 0 ? (
                  <button
                    type="button"
                    className="btn-agregar"
                    onClick={agregarPublicacion}
                  >
                    + Agregar Publicación
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-eliminar"
                    onClick={() => eliminarPublicacion(index)}
                  >
                    Eliminar
                  </button>
                )}
              </div>

              <hr className="form-divider" />
            </div>
          ))}
        </div>

        {/* === PUESTOS DESEMPEÑADOS === */}
        <div className="form-section">
          <h3>Puestos Desempeñados</h3>

          {puestos.map((puesto, index) => (
            <div key={index} className="formacion-block">
              <div className="form-row">
                <div className="form-group col-2">
                  <label>Institución</label>
                  <input
                    type="text"
                    value={puesto.institucion_empresa}
                    onChange={(e) =>
                      handleChangePuesto(
                        index,
                        "institucion_empresa",
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className="form-group col-2">
                  <label>Unidad / Proyecto</label>
                  <input
                    type="text"
                    value={puesto.unidad_proy}
                    onChange={(e) =>
                      handleChangePuesto(index, "unidad_proy", e.target.value)
                    }
                  />
                </div>

                <div className="form-group col-2">
                  <label>Puesto</label>
                  <input
                    type="text"
                    value={puesto.puesto}
                    onChange={(e) =>
                      handleChangePuesto(index, "puesto", e.target.value)
                    }
                  />
                </div>

                <div className="form-group col-1">
                  <label>Fecha inicio</label>
                  <input
                    type="date"
                    value={puesto.desde}
                    onChange={(e) =>
                      handleChangePuesto(index, "desde", e.target.value)
                    }
                  />
                </div>

                <div className="form-group col-1">
                  <label>Fecha fin</label>
                  <input
                    type="date"
                    value={puesto.hasta}
                    onChange={(e) =>
                      handleChangePuesto(index, "hasta", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-row botones-row">
                {index === 0 ? (
                  <button
                    type="button"
                    className="btn-agregar"
                    onClick={agregarPuesto}
                  >
                    + Agregar Puesto
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-eliminar"
                    onClick={() => eliminarPuesto(index)}
                  >
                    Eliminar
                  </button>
                )}
              </div>

              <hr className="form-divider" />
            </div>
          ))}
        </div>

        {/* === BOTÓN GUARDAR === */}
        <div className="form-footer">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            className="btn-guardar"
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Registrando...
              </>
            ) : (
              "Guardar Registro"
            )}
          </Button>
        </div>
      </form>

      {/* === SNACKBAR DE NOTIFICACIONES === */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={success ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {success
            ? "¡Registro exitoso! El socio ha sido registrado correctamente."
            : error || "Error al registrar socio"}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RegistroSocios;
